import {
  Card,
  ChevronRightIcon,
  FontSizeEnum,
  FontWeightEnum,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuiv4 } from 'uuid';

import { FooterFormActions } from '@/components/FooterFormActions/FooterFormActions';
import { InputArea } from '@/components/FormFields/InputArea';
import { InputDate } from '@/components/FormFields/InputDate';
import { RowContainer } from '@/components/FormFields/RowContainer';
import { SelectForm } from '@/components/FormFields/SelectForm';
import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { CLIP_REVIEW_STATES } from '@/constants';
import { AddLogos } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/AddLogos/AddLogos';
import { AddPeople } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/AddPeople/AddPeople';
import { AddStatements } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/AddStatements/AddStatements';
import {
  TSegmentDeclarationClip,
  TSegmentLogoClip,
  TSegmentPeopleClip,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/types';
import { useNavigationToolkitItem } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/hooks/useNavigationToolkitItem';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { ShowMentions } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/ShowMentions/ShowMentions';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { isCurrentTimeBetweenSegmentDates } from '@/features/broadcast-schedule-program/helpers/find-segment-by-date';
import { withResolvedImages } from '@/features/broadcast-schedule-program/helpers/with-resolved-images';
import { useDeleteClipDeclaration } from '@/features/clips/api/delete-clips-declaration';
import { useDeleteClipLogo } from '@/features/clips/api/delete-clips-logos';
import { useDeleteClipPeople } from '@/features/clips/api/delete-clips-people';
import { useGetClipsCategories } from '@/features/clips/api/get-clip-categories';
import { usePatchClip } from '@/features/clips/api/patch-clips';
import { usePatchClipLogo } from '@/features/clips/api/patch-clips-logos';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { usePostClipDeclaration } from '@/features/clips/api/post-clips-declaration';
import { usePostClipLogo } from '@/features/clips/api/post-clips-logos';
import { usePostClipPeople } from '@/features/clips/api/post-clips-people';
import { ClipReviewStateEnum, TPatchClip } from '@/features/clips/types';
import { TFeaturedPeopleType } from '@/features/featured-people/types';
import { TLogo } from '@/features/logos/types';
import { TReviewStateValues, TSegmentMention } from '@/features/segments/types';
import { useLogosById, usePeopleById } from '@/hooks/use-catalogue-entries-by-id';
import { queryClient } from '@/lib/react-query';
import { getAbsoluteDateFromPlayerTime } from '@/utils/hh-mm-ss-into-number';

type TClipCutForm = {
  body: string;
  start_datetime: Date | undefined;
  end_datetime: Date | undefined;
  seccion: string;
  review_state: TReviewStateValues;
  id: string;
};

const CLIP_CUT_FORM_DEFAULT_VALUES: TClipCutForm = {
  body: '',
  start_datetime: undefined,
  end_datetime: undefined,
  seccion: '',
  review_state: 1,
  id: '',
};

const NO_LOGOS: TLogo[] = [];
const NO_PEOPLE: TFeaturedPeopleType[] = [];

export default function ClipCut() {
  const {
    activeClip,
    clipItems,
    playerVideoCurrentTime,
    setActiveClip,
    setClipItems,
    handleTooltikClick,
    activeSegment,
    cutForm,
    selectedMention,
    selectedMentionTime,
    setSelectedMention,
  } = useBroadcastScheduleProgram();
  const { t } = useTranslation();
  const { data: clipCategories = [] } = useGetClipsCategories();
  const {
    query: { data: { data: allLogos = NO_LOGOS } = {} },
  } = useLogosById(activeClip?.clip_logos?.map(clipLogo => clipLogo.logo_id));
  const {
    query: { data: { data: allPeople = NO_PEOPLE } = {} },
  } = usePeopleById(activeClip?.clip_peoples?.map(clipPeople => clipPeople.people_id));
  const { mutateAsync: updateClip } = usePatchClip();
  const { mutateAsync: createClipLogo } = usePostClipLogo();
  const { mutateAsync: deleteClipLogo } = useDeleteClipLogo();
  const { mutateAsync: patchClipLogo } = usePatchClipLogo();
  const { mutateAsync: createClipPeople } = usePostClipPeople();
  const { mutateAsync: deleteClipPeople } = useDeleteClipPeople();
  const { mutateAsync: patchClipPeople } = usePatchClipPeople();
  const { mutateAsync: createClipDeclaration } = usePostClipDeclaration();
  const { mutateAsync: deleteClipDeclaration } = useDeleteClipDeclaration();

  const [logos, setLogos] = useState<TSegmentLogoClip[]>([]);
  const [people, setPeople] = useState<TSegmentPeopleClip[]>([]);
  const [statements, setStatements] = useState<TSegmentDeclarationClip[]>([]);
  const [mentions, setMentions] = useState<TSegmentMention[]>([]);
  const [showStatementSelect, setShowStatementSelect] = useState(false);
  const [showWarningStatements, setShowWarningStatements] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clipCategoriesOptions = useMemo(() => {
    return clipCategories.map(category => ({
      label: category.name,
      value: category.id,
    }));
  }, [clipCategories]);

  const form = useForm<TClipCutForm>({
    mode: 'all',
    defaultValues: CLIP_CUT_FORM_DEFAULT_VALUES,
  });

  const {
    hasNextItem: hasNextClip,
    handleNextItem: handleNextClip,
    handleNextItemWithoutIncidence,
  } = useNavigationToolkitItem({
    items: clipItems,
    activeItem: activeClip,
    onChangeData: setActiveClip,
    isItemDisabled: clip => clip.review_state === ClipReviewStateEnum.INCIDENCE,
  });

  const endDate = cutForm.watch('end_datetime');
  const startDate = cutForm.watch('start_datetime');

  const startTimeValue = useWatch({
    control: form.control,
    name: 'start_datetime',
  });
  const endTimeValue = useWatch({
    control: form.control,
    name: 'end_datetime',
  });
  const statusValue = useWatch({
    control: form.control,
    name: 'review_state',
  });

  const isAudio = !activeClip?.media_type?.includes('tv');

  const handleClickExit = useCallback(() => {
    form.reset(CLIP_CUT_FORM_DEFAULT_VALUES);
    setSelectedMention('');
  }, [form, setSelectedMention]);

  useEffect(() => {
    if (!selectedMentionTime || !startTimeValue || !endTimeValue) {
      setShowWarningStatements(false);
      return;
    }

    const startTime = new Date(startTimeValue).getTime();
    const endTime = new Date(endTimeValue).getTime();
    const mentionTime = new Date(selectedMentionTime).getTime();

    setShowWarningStatements(mentionTime < startTime || mentionTime > endTime);
  }, [selectedMentionTime, startTimeValue, endTimeValue]);

  useEffect(() => {
    if (selectedMention && !showStatementSelect) {
      const currentBody = form.getValues('body') || '';
      form.setValue('body', `${currentBody} ${selectedMention}`.trim());
    }
  }, [form, selectedMention, showStatementSelect]);

  const handleSubmit = async (data: TClipCutForm) => {
    const payload: TPatchClip = {
      id: data.id,
      body: data.body,
      start_datetime: data.start_datetime,
      end_datetime: data.end_datetime,
      review_state: data.review_state,
      clip_category_id: String(data.seccion),
    };

    setIsSubmitting(true);
    try {
      const newData = await updateClip(payload);

      const originalLogos: TSegmentLogoClip[] = activeClip?.clip_logos || [];
      const originalPeople: TSegmentPeopleClip[] = activeClip?.clip_peoples || [];
      const originalStatements: TSegmentDeclarationClip[] = activeClip?.clip_declarations || [];

      const logosToAdd = logos.filter(logo => !originalLogos.some(orig => orig.id === logo.id));
      const logosToDelete = originalLogos.filter(orig => !logos.some(logo => logo.id === orig.id));
      const logosToUpdate = logos.filter(logo => {
        const orig = originalLogos.find(o => o.id === logo.id);
        return orig && orig.logo_id !== logo.logo_id;
      });

      const peopleToAdd = people.filter(
        person => !originalPeople.some(orig => orig.id === person.id)
      );
      const peopleToDelete = originalPeople.filter(
        orig => !people.some(person => person.id === orig.id)
      );
      const peopleToUpdate = people.filter(person => {
        const orig = originalPeople.find(o => o.id === person.id);
        return orig && orig.people_id !== person.people_id;
      });

      const statementsToAdd = statements.filter(
        statement => !originalStatements.some(orig => orig.id === statement.id)
      );
      const statementsToDelete = originalStatements.filter(
        orig => !statements.some(statement => statement.id === orig.id)
      );

      const requests: Promise<unknown>[] = [];

      if (logosToAdd.length > 0) {
        requests.push(
          ...logosToAdd.map(logo =>
            createClipLogo({
              clip_id: newData.id || '',
              logo_id: logo.logo_id as string,
              parameters: {
                bbox: logo.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: logo.s_datetime || null,
              end_datetime: logo.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          )
        );
      }
      if (logosToDelete.length > 0) {
        requests.push(
          ...logosToDelete.map(logo => deleteClipLogo({ id: logo.id as string, silently: true }))
        );
      }
      if (logosToUpdate.length > 0) {
        requests.push(
          ...logosToUpdate.map(logo =>
            patchClipLogo({
              id: logo.id as string,
              logo_id: logo.logo_id as string,
              silently: true,
            })
          )
        );
      }
      if (peopleToAdd.length > 0) {
        requests.push(
          ...peopleToAdd.map(person =>
            createClipPeople({
              clip_id: newData.id || '',
              people_id: person.people_id as string,
              parameters: {
                bbox: person.parameters?.bbox || [0, 0, 0, 0],
              },
              start_datetime: person.s_datetime || null,
              end_datetime: person.e_datetime || null,
              entity_score: 1,
              review_state: 1,
              silently: true,
            })
          )
        );
      }
      if (peopleToDelete.length > 0) {
        requests.push(
          ...peopleToDelete.map(person =>
            deleteClipPeople({ id: person.id as string, silently: true })
          )
        );
      }
      if (peopleToUpdate.length > 0) {
        requests.push(
          ...peopleToUpdate.map(person =>
            patchClipPeople({
              id: person.id as string,
              people_id: person.people_id as string,
            })
          )
        );
      }
      if (statementsToAdd.length > 0) {
        requests.push(
          ...statementsToAdd.map(statement =>
            createClipDeclaration({
              clip_id: newData.id || '',
              text: statement.text,
              start_datetime: statement.s_datetime || null,
              end_datetime: statement.s_datetime || null,
              people_id: statement.people_id,
              entity_label: statement.entity_label || null,
              review_state: 1,
              entity_score: 1,
              silently: true,
            })
          )
        );
      }
      if (statementsToDelete.length > 0) {
        requests.push(
          ...statementsToDelete.map(statement =>
            deleteClipDeclaration({ id: statement.id as string, silently: true })
          )
        );
      }

      await Promise.all(requests);
      queryClient.invalidateQueries({ queryKey: ['clips'] });

      setClipItems(prevItems => {
        return prevItems.map(item => (item.id === newData.id ? newData : item));
      });

      // Tras guardar, navega automáticamente al siguiente clip sin incidencia.
      // Si no existe, handleNextItemWithoutIncidence no hace nada y mantenemos
      // el clip recién guardado como activo.
      setActiveClip(newData);
      handleNextItemWithoutIncidence();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (activeClip) {
      const clipStart = new Date(activeClip.start_datetime);
      const clipEnd = new Date(activeClip.end_datetime);
      form.reset({
        body: activeClip.body,
        start_datetime: clipStart,
        end_datetime: clipEnd,
        seccion: String(activeClip.clip_category_id),
        review_state: activeClip.review_state,
        id: activeClip.id,
      });
      cutForm.setValue('start_datetime', clipStart);
      cutForm.setValue('end_datetime', clipEnd);
      setLogos(
        activeClip.clip_logos?.map(clip_logo => ({
          ...clip_logo,
          tempId: uuiv4(),
          image:
            allLogos.find(logo => logo.id === clip_logo.logo_id)?.parameters
              ?.main_logo_presigned_url || '',
        })) || []
      );
      setPeople(
        activeClip.clip_peoples?.map(clip_people => ({
          ...clip_people,
          tempId: uuiv4(),
          image:
            allPeople.find(person => person.id === clip_people.people_id)?.parameters
              ?.main_logo_presigned_url || '',
        })) || []
      );
      setStatements(
        activeClip.clip_declarations?.map(clip_declaration => ({
          ...clip_declaration,
          tempId: uuiv4(),
        })) || []
      );
      setMentions(
        (activeClip.clip_mentions || []).map(mention => ({
          ...(mention as any),
          id: mention.id || '',
          segment_id: (mention as any).segment_id || '',
          text: mention.text || '',
          start_datetime: mention.start_datetime,
          end_datetime: mention.end_datetime,
          keywords: mention.keywords || [],
          clients: mention.clients || [],
          workspaces: mention.workspaces || [],
          created_at: mention.created_at || '',
          created_by: mention.created_by,
          updated_at: mention.updated_at,
          updated_by: mention.updated_by,
        }))
      );
    }
  }, [form, cutForm, activeClip]);

  // RISK: the clip's logos and people may be resolved after it was loaded. Re-seeding from `activeClip`
  // here would discard what the reviewer has changed since; only the thumbnails are patched.
  useEffect(() => {
    setLogos(prev => withResolvedImages(prev, 'logo_id', allLogos));
    setPeople(prev => withResolvedImages(prev, 'people_id', allPeople));
  }, [allLogos, allPeople]);

  const handleChangeReviewState = useCallback(
    (value: string) => {
      form.setValue('review_state', parseInt(value) as TReviewStateValues);
    },
    [form]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClickExit();
      }

      if (!activeClip?.start_datetime && !activeSegment?.start_datetime) return;

      const newDateTime = getAbsoluteDateFromPlayerTime(
        activeSegment?.start_datetime ?? activeClip!.start_datetime,
        playerVideoCurrentTime
      );

      if (event.key === 'F2') {
        form.setValue('start_datetime', newDateTime);
      }
      if (event.key === 'F9') {
        form.setValue('end_datetime', newDateTime);
      }
      if (event.key === 'F4') {
        form.setValue('body', '');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeClip, activeSegment, playerVideoCurrentTime, form]);

  useEffect(() => {
    if (!startTimeValue || !endTimeValue) return;
    form.trigger('end_datetime');
  }, [startTimeValue, endTimeValue]);

  useEffect(() => {
    if (startDate) form.setValue('start_datetime', startDate);
    if (endDate) form.setValue('end_datetime', endDate);
  }, [endDate, startDate]);

  const addToIsDisabled = useMemo(() => {
    if (!activeSegment?.start_datetime) return true;

    const segmentStart = new Date(activeSegment.start_datetime);
    const currentTime = new Date(segmentStart.getTime() + playerVideoCurrentTime * 1000);

    return !isCurrentTimeBetweenSegmentDates(
      startTimeValue ?? null,
      endTimeValue ?? null,
      currentTime
    );
  }, [activeSegment, playerVideoCurrentTime, startTimeValue, endTimeValue]);

  if (!activeClip) {
    return null;
  }

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex flex-col gap-3">
        <GeneralHeader
          name={t('Editar corte')}
          onClose={() => handleTooltikClick(ESegmentToolkit.CORTES)}
        />
        <div className={clsx('flex w-full justify-end')}>
          <StatusSeenChipSelect
            value={statusValue}
            onChange={handleChangeReviewState}
            reviewStates={CLIP_REVIEW_STATES}
          />
        </div>
        <InputArea form={form} name="body" required label={t('Descripción')} bigSize />
        <RowContainer>
          <InputDate
            form={form}
            name="start_datetime"
            required
            label={t('Inicio')}
            seconds
            disabled
            showHelpValue
          />
          <InputDate
            form={form}
            name="end_datetime"
            required
            label={t('Fin')}
            seconds
            disabled
            showHelpValue
          />
        </RowContainer>
        <RowContainer>
          <SelectForm
            form={form}
            name="seccion"
            label={t('Temática')}
            required
            options={clipCategoriesOptions}
          />
        </RowContainer>
        {!isAudio && (
          <>
            <AddLogos logos={logos} setLogos={setLogos} disabled={addToIsDisabled} />
            <hr />
            <AddPeople people={people} setPeople={setPeople} disabled={addToIsDisabled} />
            <hr />
          </>
        )}
        <AddStatements
          statements={statements}
          setStatements={setStatements}
          showStatementSelect={showStatementSelect}
          setShowStatementSelect={setShowStatementSelect}
          showWarningStatements={showWarningStatements}
        />
        <hr />
        <ShowMentions mentions={mentions} />
        <hr />
        <FooterFormActions form={form} handleSubmit={handleSubmit} isLoading={isSubmitting} />
        {hasNextClip && (
          <div className="flex w-full justify-end">
            <div className="flex cursor-pointer items-center gap-1" onClick={handleNextClip}>
              <Typography fontSize={FontSizeEnum.Sm} weight={FontWeightEnum.Semibold}>
                {t('Siguiente')}
              </Typography>

              <ChevronRightIcon />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
